import { useMemo } from "react";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  orderColumnKey,
  type OrderBoardResponse,
  type OrderDetail,
  type OrderListItem,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import type { OrderDropAction } from "@/lib/order-board";
import { buildQueryString } from "@/lib/query-string";
import { useScopedClientId } from "@/hooks/useScopedClientId";
import { useOrdersStore } from "@/stores/orders.store";

export type OrderListResponse = {
  items: OrderListItem[];
  total: number;
  page: number;
  pageSize: number;
};

/** How many cards each board column loads up front — the leads board's number. */
export const ORDERS_PER_COLUMN = 15;
/** Mirrors the server's `perColumnLimit` max in `orders.routes.ts`. */
export const MAX_ORDERS_PER_COLUMN = 200;

/**
 * The /pedidos filters as query params, the board's and the table's alike.
 *
 * `columns` is left out for the board: it hides the unselected columns
 * client-side, so every column still carries its true total — the number the
 * delete-column guard in «Columnas» trusts (the leads board does the same).
 */
function useOrderFilterParams(opts: { includeColumns: boolean }): Record<string, string> {
  const search = useOrdersStore((s) => s.search);
  const from = useOrdersStore((s) => s.from);
  const to = useOrdersStore((s) => s.to);
  const columns = useOrdersStore((s) => s.columns);
  const takenBy = useOrdersStore((s) => s.takenBy);
  const productIds = useOrdersStore((s) => s.productIds);

  // Comma-joined, so this stays a flat record the query key and the querystring
  // both consume — the `labelIds` precedent.
  const columnsKey = columns.join(",");
  const takenByKey = takenBy.join(",");
  const productIdsKey = productIds.join(",");
  return useMemo(
    () => ({
      search,
      from,
      to,
      columns: opts.includeColumns ? columnsKey : "",
      takenBy: takenByKey,
      productIds: productIdsKey,
    }),
    [search, from, to, opts.includeColumns, columnsKey, takenByKey, productIdsKey],
  );
}

/**
 * The /pedidos table: one page under the store's filters.
 *
 * Scoped on `useScopedClientId`, like `useVisits`: it is both the cache key (a
 * super-admin switching schools must not read the previous tenant's orders) and
 * the `enabled` gate. Gating on the client switcher alone is what made this list
 * come back empty for every `admin` and `member` — see that hook.
 */
export function useOrdersTable({ enabled = true }: { enabled?: boolean } = {}) {
  const clientId = useScopedClientId();
  const page = useOrdersStore((s) => s.page);
  const filters = useOrderFilterParams({ includeColumns: true });
  return useQuery({
    queryKey: ["orders", clientId, "list", filters, page],
    queryFn: () =>
      api.get<OrderListResponse>(
        `/crm/orders?${buildQueryString({ ...filters, page, pageSize: 20 })}`,
      ),
    enabled: enabled && !!clientId,
    placeholderData: keepPreviousData,
  });
}

/**
 * The /pedidos board: the first page of every column, plus each column's true
 * total. Every board cache entry sits under `["orders", clientId, "board"]`, so
 * `useMoveOrder` can update them all at once.
 */
export function useOrdersBoard({ enabled = true }: { enabled?: boolean } = {}) {
  const clientId = useScopedClientId();
  const filters = useOrderFilterParams({ includeColumns: false });
  return useQuery({
    queryKey: ["orders", clientId, "board", filters],
    queryFn: () =>
      api.get<OrderBoardResponse>(
        `/crm/orders/board?${buildQueryString({ ...filters, perColumnLimit: ORDERS_PER_COLUMN })}`,
      ),
    enabled: enabled && !!clientId,
    placeholderData: keepPreviousData,
  });
}

/**
 * One column past its first page, once someone asked for more. Disabled — and
 * `undefined` — until then, so the column renders the slice the board brought.
 */
export function useOrderColumnOverflow(column: string, limit: number | undefined) {
  const clientId = useScopedClientId();
  const filters = useOrderFilterParams({ includeColumns: false });
  const { data } = useQuery({
    queryKey: ["orders", clientId, "board", filters, column, limit],
    queryFn: () =>
      api.get<OrderBoardResponse>(
        `/crm/orders/board?${buildQueryString({ ...filters, column, perColumnLimit: limit ?? ORDERS_PER_COLUMN })}`,
      ),
    enabled: !!clientId && !!column && limit !== undefined,
  });
  return data?.items;
}

export type MoveOrderInput = {
  order: OrderListItem;
  action: Exclude<OrderDropAction, { type: "no-op" } | { type: "refused" }>;
  /** Only for `cancel`. */
  reason?: string;
};

function applyMove(item: OrderListItem, action: MoveOrderInput["action"]): OrderListItem {
  if (action.type === "board-stage") return { ...item, boardStageId: action.boardStageId };
  const now = new Date().toISOString();
  return {
    ...item,
    status: action.type === "deliver" ? "delivered" : "cancelled",
    boardStageId: null,
    closedAt: now,
  };
}

const MOVE_SUCCESS: Record<MoveOrderInput["action"]["type"], string | null> = {
  // A column change is silent: the card moving IS the confirmation.
  "board-stage": null,
  deliver: "Pedido marcado como entregado.",
  cancel: "Pedido cancelado.",
};

/**
 * Moves an order on the board — between "Por entregar" and its sub-columns, or
 * out of it as a delivery or a cancellation. The board, the mobile «Mover a…»
 * menu and the detail's column select all go through here.
 *
 * Optimistic: every board cache entry moves the card and shifts the two column
 * counts at once, and rolls back if the server refuses. The server stays the
 * authority — `onSettled` refetches everything under `["orders"]`.
 */
export function useMoveOrder() {
  const queryClient = useQueryClient();
  const clientId = useScopedClientId();
  const boardKey = ["orders", clientId, "board"];

  return useMutation({
    mutationFn: ({ order, action, reason }: MoveOrderInput) => {
      if (action.type === "board-stage") {
        return api.patch<OrderDetail>(`/crm/orders/${order.id}/board-stage`, {
          boardStageId: action.boardStageId,
        });
      }
      if (action.type === "deliver") {
        return api.post<OrderDetail>(`/crm/orders/${order.id}/deliver`, {});
      }
      return api.post<OrderDetail>(`/crm/orders/${order.id}/cancel`, reason ? { reason } : {});
    },
    onMutate: async ({ order, action }) => {
      await queryClient.cancelQueries({ queryKey: boardKey });
      const snapshots = queryClient.getQueriesData<OrderBoardResponse>({ queryKey: boardKey });

      const moved = applyMove(order, action);
      const from = orderColumnKey(order);
      const to = orderColumnKey(moved);
      queryClient.setQueriesData<OrderBoardResponse>({ queryKey: boardKey }, (old) => {
        if (!old) return old;
        return {
          items: old.items.map((i) => (i.id === order.id ? applyMove(i, action) : i)),
          counts: old.counts.map((c) =>
            c.column === from
              ? { ...c, count: Math.max(0, c.count - 1) }
              : c.column === to
                ? { ...c, count: c.count + 1 }
                : c,
          ),
        };
      });
      return { snapshots };
    },
    onSuccess: (detail, { action }) => {
      void queryClient.invalidateQueries({ queryKey: ["lead", detail.leadId] });
      const message = MOVE_SUCCESS[action.type];
      if (message) toast.success(message);
    },
    onError: (err: Error, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        queryClient.setQueryData(key, data);
      }
      toast.error(err.message || "No se pudo mover el pedido.");
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
}

export function useOrder(id: string) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["orders", clientId, "detail", id],
    queryFn: () => api.get<OrderDetail>(`/crm/orders/${id}`),
    enabled: !!clientId && !!id,
  });
}

function useOrderTransition(
  path: (id: string) => string,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      api.post<OrderDetail>(path(vars.id), vars.reason ? { reason: vars.reason } : {}),
    onSuccess: (order) => {
      // Both the list and this detail, plus the lead: an order's status is part
      // of what a lead's page shows about where the conversation ended up.
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["lead", order.leadId] });
      toast.success(successMessage);
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

export function useDeliverOrder() {
  return useOrderTransition((id) => `/crm/orders/${id}/deliver`, "Pedido marcado como entregado.");
}

export function useCancelOrder() {
  return useOrderTransition((id) => `/crm/orders/${id}/cancel`, "Pedido cancelado.");
}

/**
 * Corrects the delivery address.
 *
 * Not built on `useOrderTransition`: that one POSTs a status change and this
 * PATCHes a field, and folding them together would mean one call site's
 * "success" toast describing the other's action.
 */
export function useUpdateDeliveryAddress() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; deliveryAddress: string | null }) =>
      api.patch<OrderDetail>(`/crm/orders/${vars.id}/delivery-address`, {
        deliveryAddress: vars.deliveryAddress,
      }),
    onSuccess: (order) => {
      // The list too: its Entrega column is the copy whoever drives actually
      // reads, so leaving it stale would defeat the correction.
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      void queryClient.invalidateQueries({ queryKey: ["lead", order.leadId] });
      toast.success("Dirección de entrega actualizada.");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}

/**
 * One lead's orders, for the history on the lead detail.
 *
 * Under the `["orders", …]` key on purpose: every order mutation above already
 * invalidates `["orders"]`, so delivering or cancelling from `/pedidos/:id`
 * refreshes this list with no extra wiring. `enabled` is the caller's plan
 * check — the endpoint 403s a client without `orders`, and a lead detail must
 * not fire a request it knows will fail.
 *
 * pageSize 100 is the endpoint's ceiling, and far above what one person buys.
 */
export function useLeadOrders(leadId: string, enabled: boolean) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["orders", clientId, "lead", leadId],
    queryFn: () =>
      api.get<OrderListResponse>(
        `/crm/orders?leadId=${encodeURIComponent(leadId)}&page=1&pageSize=100`,
      ),
    enabled: enabled && !!clientId && !!leadId,
  });
}
